import { AutoSaveFeedback, ButtonV2 } from '@labrav/react-components'
import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { useCallback } from 'react'
import { isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'
import { LoadingTypes, isLoading, useAccountId } from '@labrav/common'

import { FooterV2 } from '../../../../common/components/FooterV2/FooterV2'
import { useStyles } from '../RenewOfferSubscription.styles'
import {
  isSignleFieldsHaveErr,
  checkIsErrOnRecipientOrStakeHolder,
  checkIsErrOnOfferInstallments,
  checkIsErrOnOfferDimensions,
  checkIfFormikTouchedArrayHasErr,
} from '../../../../common/utils/offerValidationHelper'
import type { AgreementBasedOffer } from '../../../../modules/subscription/reducer'
import {
  Dimension,
  EulaType,
  Installments,
  OfferRecipients,
} from '../../../../types/flyout'
import { submitRenewAgreementBasedOffer } from '../../../../modules/subscription/action'
import { useDispatch, useSelector } from '../../../../store'

export const RenewOfferSubscriptionFooter = ({
  externalBaseURL,
  selectedSubscription,
  productId,
  footerWidth,
  formikProps,
  crmOpportunityId,
  crmObjectLink,
  search,
  partnerIdFromContext,
}: {
  externalBaseURL?: string
  selectedSubscription?: string
  productId?: string
  footerWidth: string
  formikProps?: FormikProps<AgreementBasedOffer>
  crmOpportunityId?: string
  crmObjectLink?: string
  search?: string
  partnerIdFromContext?: string
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const hasExternalBaseURL = !!externalBaseURL
  const { accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const urlSearchParams = new URLSearchParams(search)
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = partnerIdFromContext || routeAccountId || fallbackAccountId
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const loadingSubmit = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.RENEW_AGREEMENT_BASED_OFFER)
  )
  const filesLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FILE_PROCESSING_LOADER)
  )
  const disableBtn = loading || filesLoading || loadingSubmit

  const isOfferSaveAsDraftDisabled = useCallback(() => {
    if (formikProps) {
      const { errors, touched } = formikProps
      const singleField = [
        'emailCustomMessage',
        'privateOfferName',
        'offerExpirationDate',
        'eulaType',
        'renewal',
      ]
      const singleFieldTouchedErr = isSignleFieldsHaveErr(
        singleField,
        errors as FormikErrors<Record<string, unknown>>,
        touched as FormikTouched<Record<string, unknown>>
      )
      const recipentErr =
        checkIfFormikTouchedArrayHasErr(
          formikProps as unknown as FormikProps<Record<string, unknown>>,
          'offerRecipients'
        ) &&
        touched?.offerRecipients?.some((recipientTouched, idx) =>
          checkIsErrOnRecipientOrStakeHolder(
            recipientTouched,
            idx,
            errors?.offerRecipients as unknown as FormikErrors<OfferRecipients>[]
          )
        )
      const installmentsErr =
        checkIfFormikTouchedArrayHasErr(
          formikProps as unknown as FormikProps<Record<string, unknown>>,
          'installments'
        ) &&
        (
          touched?.installments as unknown as FormikTouched<Installments>[]
        )?.some((installmentsTouched, idx) =>
          checkIsErrOnOfferInstallments(
            installmentsTouched,
            idx,
            errors?.installments as unknown as FormikErrors<Installments>[]
          )
        )

      const dimensionErr =
        checkIfFormikTouchedArrayHasErr(
          formikProps as unknown as FormikProps<Record<string, unknown>>,
          'dimensions'
        ) &&
        (touched?.dimensions as unknown as FormikTouched<Dimension>[])?.some(
          (dimensionTouched, idx) =>
            checkIsErrOnOfferDimensions(
              dimensionTouched,
              idx,
              errors?.dimensions as unknown as FormikErrors<Dimension>[]
            )
        )
      return (
        singleFieldTouchedErr ||
        !!recipentErr ||
        !!installmentsErr ||
        !!dimensionErr
      )
    }
    return true
  }, [formikProps])

  const isSendToBuyerDisabled = useCallback(() => {
    if (formikProps) {
      return (
        (formikProps.values.eulaType === EulaType.CUSTOM &&
          isEmpty(formikProps.values.eulaFile)) ||
        !isEmpty(formikProps.errors)
      )
    }
    return true
  }, [formikProps])

  const handleSentToBuyer = useCallback(
    (saveAsDraft: boolean) => async () => {
      if (formikProps?.values && selectedSubscription && productId) {
        const creatorEmail = urlSearchParams?.get('creator_email')
        const creatorName = urlSearchParams?.get('creator_name')
        const hasCreatorData = hasExternalBaseURL && creatorEmail && creatorName
        await dispatch(
          submitRenewAgreementBasedOffer({
            subscriptionId: selectedSubscription,
            partnerId: accountId,
            productId,
            saveAsDraft,
            crmOpportunityId,
            crmObjectLink,
            formikOffer: formikProps?.values,
            ...(hasCreatorData
              ? {
                  creatorData: {
                    creatorEmail,
                    creatorName,
                  },
                }
              : {}),
          })
        )
      }
    },
    [
      formikProps,
      productId,
      selectedSubscription,
      urlSearchParams,
      hasExternalBaseURL,
      dispatch,
      accountId,
      crmOpportunityId,
      crmObjectLink,
    ]
  )

  return (
    <FooterV2
      actions={
        <div
          className={classes.renewOfferFooterWrapper}
          data-testid={'renew-offer-footer'}
        >
          {loadingSubmit ? (
            <AutoSaveFeedback status={'loading'} />
          ) : (
            <div></div>
          )}
          <div className={classes.btnWrapper}>
            <ButtonV2
              data-testid="save-draft-button-renew-offer"
              styleType="outlined"
              onClick={handleSentToBuyer(true)}
              disabled={disableBtn || isOfferSaveAsDraftDisabled()}
            >
              Save draft
            </ButtonV2>
            <ButtonV2
              data-testid="save-send-button-renew-offer"
              styleType="primary"
              onClick={handleSentToBuyer(false)}
              disabled={disableBtn || isSendToBuyerDisabled()}
            >
              Send to buyer
            </ButtonV2>
          </div>
        </div>
      }
      width={footerWidth}
    />
  )
}
